import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, Code2, LayoutList } from "lucide-react";
import { toast } from "sonner";

const AD_TYPES = [
  { kind: "code", label: "Code ad", desc: "Animated code typing inside the dark card.", Icon: Code2 },
  { kind: "statement", label: "Statement card", desc: "Bold headline + a staggered checklist.", Icon: LayoutList },
];

export default function VariationNew() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [kind, setKind] = useState("code");
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    api.get("/templates").then((r) => setTemplates(r.data));
  }, []);

  const create = async (template) => {
    if (creating) return;
    setCreating(true);
    try {
      const base = { campaign_id: id, template_id: template.id, name: "New variation" };
      const payload = template.kind === "statement"
        ? {
            ...base, kind: "statement",
            headline: "Nine specialized AI agents.",
            accent: "AI",
            bullets: [
              "Three layers of style context the agents read on every run.",
              "Five deterministic patch guards. Three editing modes.",
            ],
          }
        : {
            ...base, kind: "code",
            code_lines: ["// new variation", "const ads = 100;", "ship(ads);"],
            language: "javascript", type_speed: "medium",
          };
      await api.post("/variations", payload);
      toast.success("Variation created");
      navigate(`/campaigns/${id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't create variation");
      setCreating(false);
    }
  };

  const shown = templates.filter((t) => (t.kind || "code") === kind);

  return (
    <div className="p-8 max-w-5xl">
      <Link to={`/campaigns/${id}`}
        className="text-xs text-gray-500 hover:text-brand inline-flex items-center gap-1 mb-2">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to campaign
      </Link>
      <h1 className="font-lexend text-4xl text-gray-900">New ad</h1>
      <p className="text-sm text-gray-500 mt-2 mb-6 max-w-xl">
        Pick the ad type, then the canvas size. You can edit the content on the next screen.
      </p>

      {/* Ad type toggle */}
      <div className="flex flex-wrap gap-3 mb-8">
        {AD_TYPES.map(({ kind: k, label, desc, Icon }) => (
          <button
            key={k}
            onClick={() => setKind(k)}
            className={`flex items-start gap-3 rounded-xl border px-4 py-3 text-left transition-all max-w-xs ${
              kind === k ? "border-brand bg-brand-50" : "border-gray-200 hover:border-gray-300 bg-white"
            }`}
          >
            <Icon className={`h-5 w-5 mt-0.5 ${kind === k ? "text-brand" : "text-gray-400"}`} />
            <span>
              <span className={`block text-sm font-semibold ${kind === k ? "text-brand" : "text-gray-900"}`}>{label}</span>
              <span className="block text-xs text-gray-500 mt-0.5">{desc}</span>
            </span>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
        {shown.map((t) => {
          const isPortrait = t.height > t.width;
          const isSquare = t.height === t.width;
          const label = isPortrait ? "Story / Reel" : isSquare ? "Square Feed" : "Landscape";
          const platforms = isPortrait
            ? "Instagram Reels · TikTok · YouTube Shorts"
            : isSquare
            ? "Instagram Feed · Facebook Feed"
            : "YouTube · X · LinkedIn";
          const ar = t.width / t.height;
          const tileH = 140;
          const tileW = Math.max(60, Math.min(220, Math.round(tileH * ar)));
          return (
            <button
              key={t.id}
              data-testid={`size-${t.width}x${t.height}`}
              onClick={() => create(t)}
              disabled={creating}
              className="group border border-gray-200 hover:border-brand rounded-xl p-6 text-left transition-all hover:shadow-md bg-white disabled:opacity-50 disabled:hover:border-gray-200"
            >
              <div className="flex justify-center mb-5 h-[140px] items-end">
                <div
                  style={{ width: tileW, height: tileH }}
                  className="bg-gradient-to-br from-gray-100 to-gray-200 group-hover:from-brand/10 group-hover:to-brand/30 rounded-lg border border-gray-300 group-hover:border-brand transition-all"
                />
              </div>
              <p className="text-base font-semibold text-gray-900 group-hover:text-brand">{label}</p>
              <p className="text-xs text-gray-500 mt-1">{t.width} × {t.height}</p>
              <p className="text-[11px] text-gray-400 mt-2 leading-snug">{platforms}</p>
            </button>
          );
        })}
        {shown.length === 0 && (
          <p className="text-sm text-gray-400 col-span-3 py-8 text-center">
            No {kind} templates yet.{" "}
            {kind === "statement" && <Link to="/templates" className="text-brand hover:underline">Upload one →</Link>}
          </p>
        )}
      </div>
    </div>
  );
}
